import { createContext, useEffect, useReducer } from 'react';
import type { FC, ReactNode } from 'react';
import PropTypes from 'prop-types';
import { userGetInitialization } from '../api/account';
import { getLocalToken } from '../services/token.service';
import AuthService from '../services/auth.service';
import ApiService from '../services/api.service';
import { initialize } from 'src/slices/authSlice';
import { Role, Scope, roleCan, accessRightsFromRole } from '../lib/rbac';

interface State {
  isInitialized: boolean;
  isAuthenticated: boolean;
  user: any | null;
  accessRights: any;
  role: Role | null;
  projects: any[];
  activeProject: any;
  welcomeMessageRead: boolean;
}

export type LoginResult =
  | { kind: 'logged_in' }
  | { kind: 'tfa_required'; email: string };

interface AuthContextValue extends State {
  method: 'JWT';
  can: (scope: Scope) => boolean;
  isProjectAdmin: boolean;
  canEditReviews: boolean;
  // initialize: () => Promise<void>;
  login: (email: string, password: string) => Promise<LoginResult>;
  finishLoginAfter2FA: () => Promise<void>;
  reloadInitialization: () => Promise<void>;
  logout: () => Promise<void>;
  welcomeMessageReadQuery: () => Promise<void>;
  register: (email: string, name: string, surname: string, password: string) => Promise<void>;
}

interface AuthProviderProps {
  children: ReactNode;
}

type InitializeAction = {
  type: 'INITIALIZE';
  payload: {
    isAuthenticated: boolean;
    user: any | null;
    accessRights: any;
    role: Role | null;
    projects: any[];
    activeProject: any;
    welcomeMessageRead: boolean;
  };
};

type LoginAction = {
  type: 'LOGIN';
  payload: {
    user: any;
  };
};

type LogoutAction = {
  type: 'LOGOUT';
};

type WelcomeMessageReadAction = {
  type: 'WELCOMEMESSAGEREAD';
};

type RegisterAction = {
  type: 'REGISTER';
  payload: {
    user: any;
  };
};

type Action =
  | InitializeAction
  | LoginAction
  | LogoutAction
  | RegisterAction
  | WelcomeMessageReadAction;

const initialState: State = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  accessRights: null,
  role: null,
  projects: [],
  activeProject: null,
  welcomeMessageRead: false
};

const handlers: Record<string, (state: State, action: Action) => State> = {
  INITIALIZE: (state: State, action: InitializeAction): State => {
    const { isAuthenticated, user, accessRights, role, projects, activeProject, welcomeMessageRead } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
      accessRights,
      role,
      projects,
      activeProject,
      welcomeMessageRead
    };
  },
  LOGIN: (state: State, action: LoginAction): State => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: false,
      user
    };
  },
  LOGOUT: (state: State): State => ({
    ...state,
    isAuthenticated: false,
    user: null
  }),
  WELCOMEMESSAGEREAD: (state: State): State => ({
    ...state,
    welcomeMessageRead: true
  }),
  REGISTER: (state: State, action: RegisterAction): State => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user
    };
  }
};

const reducer = (state: State, action: Action): State => (
  handlers[action.type] ? handlers[action.type](state, action) : state
);

export const AuthContext = createContext<AuthContextValue>({
  ...initialState,
  method: 'JWT',
  can: () => false,
  isProjectAdmin: false,
  canEditReviews: false,
  // initialize: () => Promise.resolve(),
  login: () => Promise.resolve({ kind: 'logged_in' }),
  finishLoginAfter2FA: () => Promise.resolve(),
  reloadInitialization: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  welcomeMessageReadQuery: () => Promise.resolve(),
  register: () => Promise.resolve()
});

export const AuthProvider: FC<AuthProviderProps> = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const initialize = async (): Promise<void> => {
      try {
        // const accessToken = getLocalToken();

        const info = await userGetInitialization();
        console.log(info)

        if (info !== null && info !== undefined) {
          const user = info["user"];
          const projects = info["projects"] || [];

          // Check if active_project exists and has a project property
          let accessRights = null;
          let role: Role | null = null;
          let activeProject = null;

          if (info["active_project"] && info["active_project"]["project"]) {
            // Role is the source of truth; accessRights is derived from it for
            // backward compat. Fall back to the legacy ints if role is absent.
            role = (info["active_project"]["role"] as Role) || null;
            accessRights = role ? accessRightsFromRole(role) : {
              "project_admin_space": info["active_project"]["project_admin_space"],
              "review_1": info["active_project"]["review_1"],
              "review_2": info["active_project"]["review_2"],
              "review_3": info["active_project"]["review_3"],
              "review_4": info["active_project"]["review_4"],
            };
            activeProject = info["active_project"]["project"];
          }

          // const welcomeMessageRead = user["welcome_message_read"];
          const welcomeMessageRead = true;

          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              user,
              accessRights,
              role,
              projects,
              activeProject,
              welcomeMessageRead
            }
          });
        } else {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null,
              accessRights: null,
              role: null,
              projects: [],
              activeProject: null,
              welcomeMessageRead: false
            }
          });
        }
        // if (document.cookie.match(/^(.*;)?\s*session\s*=\s*[^;]+(.*)?$/)) {
        //   // const user = await authApi.me(accessToken);
        //   const info = await userGetInitialization();
        //   const user = info["result"];
        //   const accessRights = info["active_project"]["access_rights"];
        //   const activeProject = info["active_project"]["project"];
        //   const projects = info["projects"];
        //   // const welcomeMessageRead = user["welcome_message_read"];
        //   const welcomeMessageRead = true;

        //   // localStorage.setItem('token', info["token"]);
        //   // localStorage.setItem('refresh_token', info["refresh_token"]);

        //   dispatch({
        //     type: 'INITIALIZE',
        //     payload: {
        //       isAuthenticated: true,
        //       user,
        //       accessRights,
        //       projects,
        //       activeProject,
        //       welcomeMessageRead
        //     }
        //   });
        // } else {
        //   dispatch({
        //     type: 'INITIALIZE',
        //     payload: {
        //       isAuthenticated: false,
        //       user: null,
        //       accessRights: null,
        //       projects: [],
        //       activeProject: null,
        //       welcomeMessageRead: false
        //     }
        //   });
        // }
      } catch (err) {
        console.error(err);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
            accessRights: null,
            role: null,
            projects: [],
            activeProject: null,
            welcomeMessageRead: false
          }
        });
      }
    };

    initialize();
  }, []);

  // finishLoginAfter2FA: pulls the user + project info and dispatches INITIALIZE.
  // Called either directly by login() (no-2FA path) or by the magic-link
  // callback page / recovery-code form once the session is established.
  const finishLoginAfter2FA = async (signInData?: any): Promise<void> => {
    if (signInData) {
      // SignInResponseData from the no-2FA login path — we already have it.
      localStorage.setItem('it_user', JSON.stringify(signInData));
      if (signInData.projects && signInData.projects[0]) {
        localStorage.setItem("currency", signInData.projects[0]["currency"]);
      }
      dispatch({ type: 'LOGIN', payload: { user: signInData } });
    }

    const info = await userGetInitialization();
    console.log(info)

    if (info !== null && info !== undefined) {
      const user = info["user"];
      const projects = info["projects"] || [];

      let accessRights = null;
      let role: Role | null = null;
      let activeProject = null;

      if (info["active_project"] && info["active_project"]["project"]) {
        role = (info["active_project"]["role"] as Role) || null;
        accessRights = role ? accessRightsFromRole(role) : {
          "project_admin_space": info["active_project"]["project_admin_space"],
          "review_1": info["active_project"]["review_1"],
          "review_2": info["active_project"]["review_2"],
          "review_3": info["active_project"]["review_3"],
          "review_4": info["active_project"]["review_4"],
        };
        activeProject = info["active_project"]["project"];
      }

      const welcomeMessageRead = true;

      dispatch({
        type: 'INITIALIZE',
        payload: {
          isAuthenticated: true,
          user,
          accessRights,
          role,
          projects,
          activeProject,
          welcomeMessageRead
        }
      });
    } else {
      dispatch({
        type: 'INITIALIZE',
        payload: {
          isAuthenticated: false,
          user: null,
          accessRights: null,
          role: null,
          projects: [],
          activeProject: null,
          welcomeMessageRead: false
        }
      });
    }
  };

  const login = async (email: string, password: string): Promise<LoginResult> => {
    const response = await AuthService.api.post(
      '/auth/sign-in',
      JSON.stringify({ email, password })
    );

    // 2FA branch — no session was created. The caller (LoginJwt) is responsible
    // for showing the "check your email" screen and letting the user paste a
    // recovery code instead.
    if (response.data && response.data.tfa_required) {
      return { kind: 'tfa_required', email: response.data.email || email };
    }

    const data = response.data["data"];
    await finishLoginAfter2FA(data);
    return { kind: 'logged_in' };
  };

  const logout = async (): Promise<void> => {
    // localStorage.removeItem('it_user');
    // localStorage.removeItem('token');
    // localStorage.removeItem('refresh_token');
    // dispatch({ type: 'LOGOUT' });
    // TODO
    await ApiService.post('/auth/sign-out')
      .then(async (response) => {
        window.location.href = '/login';
        dispatch({
          type: 'LOGOUT'
        });
      })
      .catch((err) => {
        console.error(err);
      })
  };

  const welcomeMessageReadQuery = async (): Promise<void> => {
    // const accessToken = await authApi.login({ email, password });
    // const user = await authApi.me(accessToken);

    await ApiService.put('/users/welcome-message-read')
      .then((response) => {
        dispatch({
          type: 'WELCOMEMESSAGEREAD'
        });
      })
  };

  const register = async (
    email: string,
    name: string,
    surname: string,
    password: string
  ): Promise<void> => {
    await AuthService.api.post('/auth/sign-up', JSON.stringify({ email: email, name: name, surname: surname, password: password }))
      .then(async (response) => {
        window.location.href = '/login';
      })
      .catch((err) => {
        console.error(err);
      })
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'JWT',
        can: (scope: Scope) => roleCan(state.role, scope),
        isProjectAdmin: roleCan(state.role, 'project:admin'),
        canEditReviews: roleCan(state.role, 'review:write'),
        // initialize,
        login,
        finishLoginAfter2FA: () => finishLoginAfter2FA(),
        // Re-fetches /auth/initialization and refreshes activeProject (e.g. after
        // project settings like review_max change) so the UI updates without reload.
        reloadInitialization: () => finishLoginAfter2FA(),
        logout,
        welcomeMessageReadQuery,
        register
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired
};
